import usePageTitle from "../../core/hooks/UsePageTitle";
import DefaultLayout from "../layouts/default/Default.layout";
import { Col, Row, Button, Input, notification, Switch, Dropdown, Modal, Select, Spin } from "antd";
import type { MenuProps } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { Certame } from "../../sdk/@types";
import CertameTable from "../components/CertameTable/CertameTable";
import { Column, useTable } from "react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChartOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  MoreOutlined,
  PlusOutlined,
  SnippetsOutlined,
  StopOutlined,
} from "@ant-design/icons";
import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  type LucideIcon,
  Users,
} from "lucide-react";
import CertameService from "../../sdk/services/CertameService";
import CertameStatsDrawer from "../components/CertameStatsDrawer/CertameStatsDrawer";
import { FilterChip, FilterChipGroup } from "../components/filters";
import { CertameAvatar } from "../components/CertameAvatar/CertameAvatar";
import { CertameKpiTile } from "../components/CertameKpiTile/CertameKpiTile";

/* ===================== HELPERS ===================== */

const numberFormatter = new Intl.NumberFormat("pt-BR");

type CertameStatusFilter = "all" | "active" | "inactive" | "pending";

// pendências = tags do certame marcadas "precisa de revisão" OU alguma
// matéria vinculada cujo subjectId não existe mais (certame-catalog/spec.md).
function hasPending(row: Certame.CatalogRow): boolean {
  return (
    (row.pendingTagReviewCount ?? 0) > 0 || (row.orphanSubjectCount ?? 0) > 0
  );
}

// Instituição é derivada da sigla, ex. "CHOA/CBMGO" → "CBMGO" — não existe
// campo próprio para isto hoje (certame-catalog/spec.md, "Filtro por instituição").
function institutionOf(abbreviation?: string): string | null {
  if (!abbreviation) return null;
  const slashIndex = abbreviation.lastIndexOf("/");
  if (slashIndex === -1) return null;
  const institution = abbreviation.slice(slashIndex + 1).trim();
  return institution || null;
}


function PendenciasCell({ row }: { row: Certame.CatalogRow }) {
  const pending = row.pendingTagReviewCount ?? 0;
  const orphan = row.orphanSubjectCount ?? 0;

  if (pending === 0 && orphan === 0) {
    return <span className="text-ink-muted">Tudo certo</span>;
  }

  return (
    <div className="flex flex-col gap-0.5 text-warn-ink">
      {pending > 0 && (
        <span>{`${numberFormatter.format(pending)} tag${pending === 1 ? "" : "s"} para revisar`}</span>
      )}
      {orphan > 0 && (
        <span>{`${numberFormatter.format(orphan)} matéria${orphan === 1 ? "" : "s"} não encontrada${orphan === 1 ? "" : "s"}`}</span>
      )}
    </div>
  );
}

/* ===================== COMPONENT ===================== */

export default function CertameView() {
  usePageTitle("Certame");

  const navigate = useNavigate();

  // Lista enriquecida (design.md "Service/type additions") — carregada aqui,
  // não via useCertame/redux, porque os KPIs e os filtros desta tela precisam
  // dos contadores que só `getCatalog()` devolve.
  const [certames, setCertames] = useState<Certame.CatalogRow[]>([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<CertameStatusFilter>("all");
  const [institutionFilter, setInstitutionFilter] = useState<string | null>(null);

  // certame whose linked-questions stats drawer is open (null = closed)
  const [statsFor, setStatsFor] = useState<Certame.Certame | null>(null);

  /* ===================== LIST FETCH ===================== */

  const loadCertames = useCallback(() => {
    setLoadingCatalog(true);
    CertameService.getCatalog()
      .then((data) => setCertames(data ?? []))
      .catch((error) => {
        notification.error({
          message: "Erro ao carregar certames",
          description:
            error instanceof Error ? error.message : "Erro inesperado",
        });
      })
      .finally(() => setLoadingCatalog(false));
  }, []);

  useEffect(() => {
    loadCertames();
  }, [loadCertames]);

  /* ===================== ROW ACTIONS ===================== */

  const handleToggleDisabled = async (row: Certame.CatalogRow) => {
    try {
      await CertameService.setDisabled(row.id!, !row.disabled);
      notification.success({
        message: "Sucesso",
        description: `Certame "${row.abbreviation}" ${row.disabled ? "ativado" : "desativado"} com sucesso.`,
      });
      loadCertames();
    } catch (error) {
      notification.error({
        message: "Erro",
        description:
          error instanceof Error ? error.message : "Erro inesperado",
      });
    }
  };

  const handleDuplicate = async (row: Certame.CatalogRow) => {
    try {
      await CertameService.duplicate(row.id!);
      notification.success({
        message: "Sucesso",
        description: `Certame "${row.abbreviation}" duplicado com sucesso.`,
      });
      loadCertames();
    } catch (error) {
      notification.error({
        message: "Erro ao duplicar",
        description:
          error instanceof Error ? error.message : "Erro inesperado",
      });
    }
  };

  const handleCopyId = (row: Certame.CatalogRow) => {
    if (!row.id) return;
    navigator.clipboard
      .writeText(row.id)
      .then(() => {
        notification.success({
          message: "ID copiado",
          description: row.id,
        });
      })
      .catch(() => {
        notification.error({
          message: "Não foi possível copiar o ID",
          description: row.id,
        });
      });
  };

  const handleDelete = (row: Certame.CatalogRow) => {
    Modal.confirm({
      title: "Excluir certame",
      content: `Tem certeza que deseja excluir "${row.abbreviation}"? Esta ação não pode ser desfeita.`,
      okText: "Sim, excluir",
      okType: "danger",
      cancelText: "Cancelar",
      centered: true,
      onOk: async () => {
        try {
          await CertameService.delete(row);
          notification.success({
            message: "Sucesso",
            description: `Certame "${row.abbreviation}" removido com sucesso.`,
          });
          loadCertames();
        } catch (error) {
          notification.error({
            message: "Erro ao excluir",
            description:
              error instanceof Error ? error.message : "Erro inesperado",
          });
        }
      },
    });
  };

  const actionItems = (row: Certame.CatalogRow): MenuProps["items"] => [
    {
      key: "stats",
      icon: <BarChartOutlined />,
      label: "Ver estatísticas",
      onClick: () => setStatsFor(row),
    },
    {
      key: "duplicate",
      icon: <SnippetsOutlined />,
      label: "Duplicar certame",
      onClick: () => handleDuplicate(row),
    },
    {
      key: "copy-id",
      icon: <CopyOutlined />,
      label: "Copiar ID",
      onClick: () => handleCopyId(row),
    },
    { type: "divider" },
    {
      key: "toggle-disabled",
      icon: row.disabled ? <CheckCircleOutlined /> : <StopOutlined />,
      label: row.disabled ? "Ativar" : "Desativar",
      onClick: () => handleToggleDisabled(row),
    },
    {
      key: "delete",
      icon: <DeleteOutlined />,
      label: "Excluir",
      danger: true,
      onClick: () => handleDelete(row),
    },
  ];

  /* ===================== TABLE ===================== */

  const columns: Column<Certame.CatalogRow>[] = useMemo(() => {
    return [
      {
        Header: "Certame",
        Cell: ({ row }) => (
          <div className="flex items-center gap-3">
            <CertameAvatar id={row.original.id} abbreviation={row.original.abbreviation} />
            <div className="flex flex-col">
              <span className="font-semibold text-ink">
                {row.original.abbreviation}
              </span>
              <span className="text-[12px] text-ink-muted truncate max-w-[320px]">
                {row.original.description}
              </span>
            </div>
          </div>
        ),
      },
      {
        Header: "Matérias",
        Cell: ({ row }) => (
          <span className="tabular-nums">
            {numberFormatter.format(
              row.original.subjectCount ?? row.original.subjectIds?.length ?? 0,
            )}
          </span>
        ),
      },
      {
        Header: "Questões",
        Cell: ({ row }) => (
          <span className="tabular-nums">
            {numberFormatter.format(row.original.questionCount ?? 0)}
          </span>
        ),
      },
      {
        Header: "Alunos",
        Cell: ({ row }) => (
          <span className="tabular-nums">
            {numberFormatter.format(row.original.subscriberCount ?? 0)}
          </span>
        ),
      },
      {
        Header: "Pendências",
        Cell: ({ row }) => <PendenciasCell row={row.original} />,
      },
      {
        Header: "Status",
        Cell: ({ row }) => {
          const disabled = Boolean(row.original.disabled);
          return (
            <div onClick={(e) => e.stopPropagation()}>
              <Switch
                checked={!disabled}
                checkedChildren="Ativo"
                unCheckedChildren="Inativo"
                onChange={() => handleToggleDisabled(row.original)}
              />
            </div>
          );
        },
      },
      {
        Header: "",
        id: "acoes",
        Cell: ({ row }) => (
          <div
            className="flex justify-end gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() =>
                row.original.id && navigate(`/certame/${row.original.id}`)
              }
            >
              Editar
            </Button>
            <Button
              type="text"
              icon={<BarChartOutlined />}
              onClick={() => setStatsFor(row.original)}
            >
              Estatísticas
            </Button>
            <Dropdown
              menu={{ items: actionItems(row.original) }}
              trigger={["click"]}
            >
              <Button type="text" icon={<MoreOutlined />} aria-label="Ações" />
            </Dropdown>
          </div>
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ===================== KPIs + FILTERS (computed client-side) ===================== */

  const kpis = useMemo(() => {
    const list = certames ?? [];
    return {
      activeCount: list.filter((row) => !row.disabled).length,
      questionTotal: list.reduce((sum, row) => sum + (row.questionCount ?? 0), 0),
      subscriberTotal: list.reduce((sum, row) => sum + (row.subscriberCount ?? 0), 0),
      pendingCount: list.filter(hasPending).length,
    };
  }, [certames]);

  const statusCounts = useMemo(() => {
    const list = certames ?? [];
    return {
      all: list.length,
      active: list.filter((row) => !row.disabled).length,
      inactive: list.filter((row) => row.disabled).length,
      pending: list.filter(hasPending).length,
    };
  }, [certames]);

  const institutions = useMemo(() => {
    const set = new Set<string>();
    (certames ?? []).forEach((row) => {
      const institution = institutionOf(row.abbreviation);
      if (institution) set.add(institution);
    });
    return Array.from(set).sort();
  }, [certames]);

  const institutionCounts = useMemo(() => {
    const counts = new Map<string, number>();
    (certames ?? []).forEach((row) => {
      const institution = institutionOf(row.abbreviation);
      if (!institution) return;
      counts.set(institution, (counts.get(institution) ?? 0) + 1);
    });
    return counts;
  }, [certames]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (certames ?? []).filter((row) => {
      if (term) {
        const haystack = `${row.abbreviation ?? ""} ${row.description ?? ""}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }

      if (statusFilter === "active" && row.disabled) return false;
      if (statusFilter === "inactive" && !row.disabled) return false;
      if (statusFilter === "pending" && !hasPending(row)) return false;

      if (institutionFilter && institutionOf(row.abbreviation) !== institutionFilter) {
        return false;
      }

      return true;
    });
  }, [certames, search, statusFilter, institutionFilter]);

  const instance = useTable<Certame.CatalogRow>({
    columns,
    data: filteredRows,
  });

  /* ===================== UI ===================== */

  return (
    <DefaultLayout>
      <div>
        {/* ── PAGE HEADER ── */}
        <Row gutter={16}>
          <div className="flex w-full items-start justify-between gap-4 mb-2 border-b border-edge p-2">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-secondary">
                Administração
              </p>
              <div className="flex items-center gap-2">
                <p className="text-2xl font-bold text-ink">Certames</p>
                <span className="inline-flex items-center rounded-chip bg-surface-muted px-2 py-0.5 text-[12px] font-semibold tabular-nums text-ink-secondary">
                  {numberFormatter.format(certames.length)}
                </span>
              </div>
              <p className="text-1x1 font-normal text-ink-secondary">
                Manutenção do cadastro de certame
              </p>
            </div>
            <Button
              type="primary"
              onClick={() => navigate("/certame/novo")}
              icon={<PlusOutlined />}
            >
              Novo certame
            </Button>
          </div>
        </Row>

        {/* ── KPIs ── */}
        <Row gutter={16}>
          <div className="flex w-full flex-wrap gap-3 p-2">
            <CertameKpiTile
              label="Certames ativos"
              value={numberFormatter.format(kpis.activeCount)}
              icon={CheckCircle2}
              accent="blue"
            />
            <CertameKpiTile
              label="Questões vinculadas"
              value={numberFormatter.format(kpis.questionTotal)}
              icon={BookOpen}
              accent="green"
            />
            <CertameKpiTile
              label="Alunos inscritos"
              value={numberFormatter.format(kpis.subscriberTotal)}
              icon={Users}
              accent="purple"
            />
            <CertameKpiTile
              label="Certames com pendências"
              value={numberFormatter.format(kpis.pendingCount)}
              icon={AlertTriangle}
              tone={kpis.pendingCount > 0 ? "warn" : "neutral"}
            />
          </div>
        </Row>

        {/* ── SEARCH + FILTERS ── */}
        <Row gutter={16}>
          <div className="flex w-full flex-col gap-3 p-2">
            <Input
              allowClear
              placeholder="Buscar por sigla ou descrição…"
              prefix={<SearchOutlined className="text-ink-muted" />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-md"
            />

            <div className="inline-flex w-fit rounded-input bg-surface-muted p-1">
              <FilterChipGroup role="radiogroup" ariaLabel="Status do certame">
                <FilterChip
                  role="radio"
                  label={`Todos · ${numberFormatter.format(statusCounts.all)}`}
                  selected={statusFilter === "all"}
                  onToggle={() => setStatusFilter("all")}
                />
                <FilterChip
                  role="radio"
                  label={`Ativos · ${numberFormatter.format(statusCounts.active)}`}
                  selected={statusFilter === "active"}
                  onToggle={() => setStatusFilter("active")}
                />
                <FilterChip
                  role="radio"
                  label={`Inativos · ${numberFormatter.format(statusCounts.inactive)}`}
                  selected={statusFilter === "inactive"}
                  onToggle={() => setStatusFilter("inactive")}
                />
                <FilterChip
                  role="radio"
                  label={`Com pendências · ${numberFormatter.format(statusCounts.pending)}`}
                  selected={statusFilter === "pending"}
                  onToggle={() => setStatusFilter("pending")}
                />
              </FilterChipGroup>
            </div>

            {institutions.length > 0 && (
              <Select
                aria-label="Instituição"
                value={institutionFilter ?? "all"}
                style={{ minWidth: 220 }}
                onChange={(value) =>
                  setInstitutionFilter(value === "all" ? null : value)
                }
                options={[
                  {
                    value: "all",
                    label: "Instituição: Todas",
                    display: "Todas",
                  },
                  ...institutions.map((institution) => ({
                    value: institution,
                    label: `Instituição: ${institution}`,
                    display: `${institution} · ${numberFormatter.format(
                      institutionCounts.get(institution) ?? 0,
                    )}`,
                  })),
                ]}
                optionRender={(option) => option.data.display}
              />
            )}
          </div>
        </Row>

        {/* ── TABLE ── */}
        <Row gutter={16}>
          <Col span={24} style={{ marginTop: "20px" }}>
            {loadingCatalog ? (
              <div className="flex justify-center py-16">
                <Spin />
              </div>
            ) : (
              <CertameTable
                instance={instance}
                onRowClick={(row) => row.id && navigate(`/certame/${row.id}`)}
              />
            )}
          </Col>
        </Row>

        {/* ── TABLE FOOTER ── */}
        <Row gutter={16}>
          <div className="flex w-full items-center justify-between px-2 py-3 text-[12px] text-ink-secondary">
            <span>
              {`Mostrando ${numberFormatter.format(filteredRows.length)} de ${numberFormatter.format(certames.length)} certames`}
            </span>
            <span>Clique na linha para editar</span>
          </div>
        </Row>
      </div>

      {/* ── STATS DRAWER ── */}
      <CertameStatsDrawer
        open={statsFor !== null}
        certame={statsFor}
        onClose={() => setStatsFor(null)}
      />
    </DefaultLayout>
  );
}
